import React from 'react';
import TableauDeBord from '../TableauDeBord/TableauDeBord';
import StatutBadge from '../StatutBadge/StatutBadge';
import { formatDate } from '../../utils/entityUtils';
import './TableauComplements.css';

// Une ligne du tableau « Commandes complementaires » : la principale ou un complement, telle que
// servie par le bdc (LigneComplementResource). `createur` est resolu par le core, `cree_par` est le
// libelle brut de repli.
export interface LigneComplement {
  id: number;
  numero?: string | number;
  nom?: string;
  createur?: string | null;
  cree_par?: string | null;
  prix_total?: string | number | null;
  created_at?: string;
  /** Libelle de statut pret a afficher, calcule par chaque cote (statut de l'operation ou de sa commande). */
  statut?: string | null;
}

interface TableauComplementsProps<L extends LigneComplement> {
  lignes: L[];
  /** Ligne de la page ouverte : en gras, jamais un lien. */
  idCourant: number;
  /** Une ligne qui ne s'ouvre pas reste visible, sans lien, avec `mentionNonOuvrable` sous son numero. */
  estOuvrable: (ligne: L) => boolean;
  onOuvrir: (ligne: L) => void;
  mentionNonOuvrable?: string;
}

/** Montant HT d'une ligne : 0,00 EUR tant que le panier est vide (complement en brouillon). */
const montant = (valeur?: string | number | null): string =>
  `${Number(valeur ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT`;

// Tableau Date | Numero | Nom de l'operation | Createur | Montant | Statut sur le composant partage
// TableauDeBord (celui de l'onglet Historique), sans tri ni pagination : les lignes arrivent deja
// dans l'ordre metier, la principale puis ses complements.
function TableauComplements<L extends LigneComplement>({
  lignes,
  idCourant,
  estOuvrable,
  onOuvrir,
  mentionNonOuvrable,
}: TableauComplementsProps<L>) {
  const parId = new Map(lignes.map((ligne) => [String(ligne.id), ligne]));

  const numero = (ligne: L) => {
    const texte = String(ligne.numero ?? '');
    if (ligne.id === idCourant) return <span className="tab-complements__courante">{texte}</span>;
    if (estOuvrable(ligne)) return <span className="tab-complements__lien">{texte}</span>;
    return (
      <span className="tab-complements__ferme">
        {texte}
        {mentionNonOuvrable && <small>{mentionNonOuvrable}</small>}
      </span>
    );
  };

  const colonnes = [
    {
      field: 'created_at',
      header: 'Date',
      renderCell: (_: unknown, ligne: L) => (ligne.created_at ? formatDate(ligne.created_at) : '-'),
    },
    { field: 'numero', header: 'Numéro', renderCell: (_: unknown, ligne: L) => numero(ligne) },
    { field: 'nom', header: "Nom de l'opération" },
    {
      field: 'createur',
      header: 'Créateur',
      renderCell: (_: unknown, ligne: L) => ligne.createur || ligne.cree_par || '-',
    },
    { field: 'prix_total', header: 'Montant', renderCell: (_: unknown, ligne: L) => montant(ligne.prix_total) },
    { field: 'statut', header: 'Statut', renderCell: (_: unknown, ligne: L) => <StatutBadge libelle={ligne.statut} /> },
  ];

  const ouvrir = (id: string) => {
    const ligne = parId.get(String(id));
    if (ligne && ligne.id !== idCourant && estOuvrable(ligne)) onOuvrir(ligne);
  };

  return (
    <TableauDeBord
      data={lignes}
      columns={colonnes}
      onSearch={() => {}}
      onRowClick={ouvrir}
      showActionsColumn={false}
      isSelectable={false}
      selectedItems={[]}
      onSelectionChange={() => {}}
      showPagination={false}
      showTotalCount={false}
      disableDefaultSort
    />
  );
}

export default TableauComplements;
